import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  Loader,
  MessageSquare,
  Coins,
  Calendar,
  Crown,
  Settings,
  Shield, // Replaced DollarSign
  Image as ImageIcon,
  ToggleLeft,
  ToggleRight,
  Edit3,
  Save,
  Trash2,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const getAuthToken = () => {
  return localStorage.getItem("accessToken") || "mock-token";
};

const ChatbotManagement = () => {
  const { isDarkMode } = useTheme();

  const [activeTab, setActiveTab] = useState("subscriptions");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Subscription Plans state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    planType: "",
    name: "",
    price: "",
    currency: "INR",
    discount: "",
    durationDays: "",
    dailyTokens: "",
    dailyImageLimit: "",
    description: "",
    features: "",
  });

  // App Config state
  const [chatbotConfig, setChatbotConfig] = useState({
    dailyFreeCoins: 20000,
    isActive: true, // ✅ NEW
    textMessageCost: 3,
    imageGenerationCost: 2000,
    useDynamicTokenDeduction: true,
    freeTierDailyImageLimit: 2,
    premiumTierDailyImageLimit: 20,
  });
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    dailyFreeCoins: 20000,
    isActive: true, // ✅ NEW
    textMessageCost: 3,
    imageGenerationCost: 2000,
    useDynamicTokenDeduction: true,
    freeTierDailyImageLimit: 2,
    premiumTierDailyImageLimit: 20,
  });

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    plan: null,
  });

  const handleDeleteClick = (plan) => {
    setDeleteConfirmation({
      show: true,
      plan: plan,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.plan) return;

    try {
      await apiCall(
        `/admin/subscription-plans/${deleteConfirmation.plan.planType}`,
        {
          method: "DELETE",
        },
      );
      setSuccess("Plan deleted successfully");
      fetchSubscriptionPlans();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirmation({ show: false, plan: null });
    }
  };

  // API Helper
  const apiCall = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          ...options.headers,
        },
      });

      const responseText = await response.text();
      let data = null;

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          if (
            responseText.trim().startsWith("<!DOCTYPE") ||
            responseText.trim().startsWith("<html")
          ) {
            throw new Error(
              "API returned HTML instead of JSON. Check REACT_APP_BASE_URL and backend availability.",
            );
          }
          throw new Error("Invalid JSON response from API");
        }
      }

      if (!response.ok) {
        throw new Error(
          data?.message || `API request failed (${response.status})`,
        );
      }

      return data;
    } catch (err) {
      throw new Error(err.message || "Network error");
    }
  };

  // Fetch Subscription Plans
  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall(
        "/admin/subscription-plans?includeInactive=true",
      );
      setSubscriptionPlans(data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Chatbot Config
  const fetchChatbotConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall("/admin/config/chatbot");
      setChatbotConfig(data);
      setConfigForm(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize default data
  const initializeDefaults = async () => {
    try {
      setLoading(true);
      await Promise.all([
        apiCall("/admin/subscription-plans/initialize", { method: "POST" }),
        apiCall("/admin/config/initialize", { method: "POST" }),
      ]);
      setSuccess("Default data initialized successfully!");
      fetchSubscriptionPlans();
      fetchChatbotConfig();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create or Update Subscription Plan
  const handleSavePlan = async (e) => {
    e.preventDefault();

    if (
      !planForm.planType ||
      !planForm.name ||
      !planForm.price ||
      !planForm.durationDays
    ) {
      setError("Please fill all required fields");
      return;
    }

    try {
      setFormSubmitting(true);

      const payload = {
        planType: planForm.planType,
        name: planForm.name,
        price: parseFloat(planForm.price),
        currency: planForm.currency,
        discount: parseFloat(planForm.discount) || 0,
        durationDays: parseInt(planForm.durationDays),
        dailyTokens: parseInt(planForm.dailyTokens) || 500,
        dailyImageLimit: parseInt(planForm.dailyImageLimit) || 20,
        description: planForm.description,
        features: planForm.features
          .split(",")
          .map((f) => f.trim())
          .filter((f) => f),
      };

      if (editingPlan) {
        await apiCall(`/admin/subscription-plans/${planForm.planType}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        setSuccess("Subscription plan updated successfully!");
      } else {
        await apiCall("/admin/subscription-plans", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setSuccess("Subscription plan created successfully!");
      }

      setShowPlanModal(false);
      setEditingPlan(null);
      resetPlanForm();
      fetchSubscriptionPlans();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Toggle Plan Status
  const handleTogglePlan = async (planType) => {
    try {
      await apiCall(`/admin/subscription-plans/${planType}/toggle`, {
        method: "PATCH",
      });
      setSuccess("Plan status updated!");
      fetchSubscriptionPlans();
    } catch (err) {
      setError(err.message);
    }
  };

  // Update Chatbot Config
  const handleSaveConfig = async (e) => {
    e.preventDefault();

    try {
      setFormSubmitting(true);

      await apiCall("/admin/config", {
        method: "PATCH",
        body: JSON.stringify({
          dailyFreeCoins: parseInt(configForm.dailyFreeCoins),
          isActive: configForm.isActive, // ✅ NEW
          textMessageCost: parseInt(configForm.textMessageCost),
          imageGenerationCost: parseInt(configForm.imageGenerationCost),
          useDynamicTokenDeduction: configForm.useDynamicTokenDeduction,
          freeTierDailyImageLimit: parseInt(configForm.freeTierDailyImageLimit),
          premiumTierDailyImageLimit: parseInt(
            configForm.premiumTierDailyImageLimit,
          ),
        }),
      });

      setSuccess("Chatbot config updated successfully!");
      setEditingConfig(false);
      fetchChatbotConfig();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      planType: "",
      name: "",
      price: "",
      currency: "INR",
      discount: "",
      durationDays: "",
      dailyTokens: "",
      dailyImageLimit: "",
      description: "",
      features: "",
    });
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      planType: plan.planType,
      name: plan.name,
      price: plan.price.toString(),
      currency: plan.currency || "INR",
      discount: plan.discount?.toString() || "",
      durationDays: plan.durationDays.toString(),
      dailyTokens: plan.dailyTokens?.toString() || "500",
      dailyImageLimit: plan.dailyImageLimit?.toString() || "20",
      description: plan.description || "",
      features: plan.features?.join(", ") || "",
    });
    setShowPlanModal(true);
  };

  // Load data on mount
  useEffect(() => {
    if (activeTab === "subscriptions") {
      fetchSubscriptionPlans();
    } else {
      fetchChatbotConfig();
    }
  }, [activeTab, fetchSubscriptionPlans, fetchChatbotConfig]);

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const themeClasses = {
    bg: isDarkMode ? "bg-black" : "bg-gray-100",
    text: isDarkMode ? "text-white" : "text-gray-900",
    card: isDarkMode ? "bg-black border-zinc-800" : "bg-white border-gray-200",
    input: isDarkMode
      ? "bg-zinc-800 border-zinc-700 text-white"
      : "bg-gray-100 border-gray-300 text-gray-900",
    hover: isDarkMode ? "hover:bg-zinc-800" : "hover:bg-gray-100",
    muted: isDarkMode ? "text-gray-400" : "text-gray-600",
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case "weekly":
        return <Calendar size={24} className="text-blue-400" />;
      case "monthly":
        return <Crown size={24} className="text-purple-400" />;
      case "yearly":
        return <Crown size={24} className="text-orange-400" />;
      default:
        return <Calendar size={24} />;
    }
  };

  if (
    loading &&
    subscriptionPlans.length === 0 &&
    !chatbotConfig.dailyFreeCoins
  ) {
    return (
      <div className={`flex flex-col min-h-screen ${themeClasses.bg}`}>
        <div
          className={`flex-grow flex items-center justify-center ${themeClasses.text}`}
        >
          <div className="text-center">
            <Loader className="animate-spin w-12 h-12 mx-auto mb-4 text-orange-400" />
            <p className="text-xl">Loading chatbot settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text}`}>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
              <MessageSquare className="text-black" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Chatbot Management</h1>
              <p className={themeClasses.muted}>
                Manage subscription plans and chatbot settings
              </p>
            </div>
          </div>
          <button
            onClick={initializeDefaults}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white font-medium rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            Initialize Defaults
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 ${
              isDarkMode
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{error}</div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 ${
              isDarkMode
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          >
            <Check size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{success}</div>
            <button
              onClick={() => setSuccess(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div
          className={`flex gap-4 border-b ${isDarkMode ? "border-zinc-800" : "border-gray-200"}`}
        >
          <button
            onClick={() => setActiveTab("subscriptions")}
            className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "subscriptions"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                  ? "border-transparent text-gray-400 hover:text-white"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Crown size={20} />
            Subscription Plans
          </button>
          <button
            onClick={() => setActiveTab("config")}
            className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "config"
                ? isDarkMode
                  ? "border-orange-400 text-orange-400"
                  : "border-orange-500 text-orange-600"
                : isDarkMode
                  ? "border-transparent text-gray-400 hover:text-white"
                  : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Settings size={20} />
            Daily Tokens & Costs
          </button>
        </div>

        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <div className="space-y-6">
            {/* Actions Bar */}
            <div className="flex justify-between items-center">
              <button
                onClick={fetchSubscriptionPlans}
                className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${
                  isDarkMode
                    ? "text-orange-400 hover:text-orange-300"
                    : "text-orange-600 hover:text-orange-700"
                }`}
                disabled={loading}
              >
                <RefreshCw
                  size={18}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
              <button
                onClick={() => {
                  resetPlanForm();
                  setEditingPlan(null);
                  setShowPlanModal(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors shadow-lg"
              >
                <Plus size={20} />
                Add Plan
              </button>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionPlans.length === 0 ? (
                <div
                  className={`col-span-full ${themeClasses.card} border rounded-xl p-12 text-center`}
                >
                  <Crown
                    size={48}
                    className={themeClasses.muted + " mx-auto mb-4"}
                  />
                  <p className={themeClasses.muted}>
                    No subscription plans found
                  </p>
                  <p className={themeClasses.muted + " text-sm mt-2"}>
                    Click "Initialize Defaults" to create default plans
                  </p>
                </div>
              ) : (
                subscriptionPlans.map((plan) => (
                  <div
                    key={plan.planType}
                    className={`${themeClasses.card} border rounded-xl p-6 transition-all hover:shadow-lg ${
                      !plan.isActive ? "opacity-60" : ""
                    }`}
                  >
                    {/* Plan Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            plan.planType === "yearly"
                              ? "bg-orange-400/20"
                              : plan.planType === "monthly"
                                ? "bg-purple-400/20"
                                : "bg-blue-400/20"
                          }`}
                        >
                          {getPlanIcon(plan.planType)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{plan.name}</h3>
                          <p
                            className={`text-xs uppercase ${themeClasses.muted}`}
                          >
                            {plan.planType}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          plan.isActive
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {plan.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Price */}
                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">
                          ₹{plan.price}
                        </span>
                        <span className={themeClasses.muted}>
                          /{plan.durationDays} days
                        </span>
                      </div>
                      <div className="mb-2 flex items-center gap-2">
                        <Coins size={16} className="text-orange-400" />
                        <span className="font-semibold text-sm">
                          {plan.dailyTokens || 500} daily tokens
                        </span>
                      </div>
                      {plan.discount > 0 && (
                        <span className="text-green-400 text-sm">
                          {plan.discount}% off
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {plan.description && (
                      <p className={`text-sm mb-4 ${themeClasses.muted}`}>
                        {plan.description}
                      </p>
                    )}

                    {/* Features */}
                    {plan.features && plan.features.length > 0 && (
                      <ul className="space-y-2 mb-4">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-center gap-2 text-sm"
                          >
                            <Check size={16} className="text-green-400" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Actions */}
                    <div
                      className={`flex items-center gap-2 pt-4 border-t ${isDarkMode ? "border-zinc-800" : "border-gray-200"}`}
                    >
                      <button
                        onClick={() => openEditPlan(plan)}
                        className={`flex-1 p-2 rounded transition-colors ${themeClasses.hover}`}
                        title="Edit"
                      >
                        <Edit3 size={20} className="text-blue-400 mx-auto" />
                      </button>
                      <button
                        onClick={() => handleTogglePlan(plan.planType)}
                        className={`flex-1 p-2 rounded transition-colors ${themeClasses.hover}`}
                        title={plan.isActive ? "Deactivate" : "Activate"}
                      >
                        {plan.isActive ? (
                          <ToggleRight
                            size={20}
                            className="text-green-400 mx-auto"
                          />
                        ) : (
                          <ToggleLeft
                            size={20}
                            className={`${themeClasses.muted} mx-auto`}
                          />
                        )}
                      </button>

                      <button
                        onClick={() => handleDeleteClick(plan)}
                        className={`flex-1 p-2 rounded transition-colors ${themeClasses.hover}`}
                        title="Delete"
                      >
                        <Trash2 size={20} className="text-red-400 mx-auto" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Config Tab */}
        {activeTab === "config" && (
          <div className="space-y-6">
            <div className={`${themeClasses.card} border rounded-xl p-6`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Settings size={24} className="text-orange-400" />
                  Chatbot Configuration
                </h2>
                {!editingConfig ? (
                  <button
                    onClick={() => setEditingConfig(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors"
                  >
                    <Edit3 size={18} />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingConfig(false);
                        setConfigForm(chatbotConfig);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium border ${
                        isDarkMode
                          ? "border-zinc-700 text-white hover:bg-zinc-800"
                          : "border-gray-300 text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      disabled={formSubmitting}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      {formSubmitting ? (
                        <RefreshCw size={18} className="animate-spin" />
                      ) : (
                        <Save size={18} />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Global Chatbot Status */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        chatbotConfig.isActive ? "bg-green-500" : "bg-red-500"
                      }`}
                    >
                      <MessageSquare className="text-white" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Global Status
                      </p>
                      <p className="font-semibold">Enable/Disable Chatbot</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <div className="flex items-center h-full">
                      <button
                        type="button"
                        onClick={() =>
                          setConfigForm({
                            ...configForm,
                            isActive: !configForm.isActive,
                          })
                        }
                        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                          configForm.isActive ? "bg-green-500" : "bg-gray-400"
                        }`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            configForm.isActive
                              ? "translate-x-7"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="ml-3 font-medium">
                        {configForm.isActive ? "Active" : "Disabled"}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xl font-bold ${chatbotConfig.isActive ? "text-green-500" : "text-red-500"}`}
                      >
                        {chatbotConfig.isActive ? "Active" : "Disabled"}
                      </span>
                    </div>
                  )}
                </div>
                {/* Daily Free Coins */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-400 rounded-full flex items-center justify-center">
                      <Coins className="text-black" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Daily Free Tokens
                      </p>
                      <p className="font-semibold">Given to users daily</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <input
                      type="number"
                      value={configForm.dailyFreeCoins}
                      onChange={(e) =>
                        setConfigForm({
                          ...configForm,
                          dailyFreeCoins: e.target.value,
                        })
                      }
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 text-2xl font-bold ${themeClasses.input}`}
                    />
                  ) : (
                    <p className="text-3xl font-bold text-orange-400">
                      {chatbotConfig.dailyFreeCoins}
                    </p>
                  )}
                </div>

                {/* Text Message Cost */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5 transition-opacity ${
                    (
                      editingConfig
                        ? configForm.useDynamicTokenDeduction
                        : chatbotConfig.useDynamicTokenDeduction
                    )
                      ? "opacity-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="text-white" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Text Message Cost
                      </p>
                      <p className="font-semibold">Tokens per message</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <input
                      type="number"
                      value={configForm.textMessageCost}
                      onChange={(e) =>
                        setConfigForm({
                          ...configForm,
                          textMessageCost: e.target.value,
                        })
                      }
                      disabled={configForm.useDynamicTokenDeduction}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 text-2xl font-bold ${themeClasses.input} ${
                        configForm.useDynamicTokenDeduction
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  ) : (
                    <p className="text-3xl font-bold text-blue-400">
                      {chatbotConfig.textMessageCost}
                    </p>
                  )}
                </div>

                {/* Image Generation Cost */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5 transition-opacity ${
                    (
                      editingConfig
                        ? configForm.useDynamicTokenDeduction
                        : chatbotConfig.useDynamicTokenDeduction
                    )
                      ? "opacity-50"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                      <ImageIcon className="text-white" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Image Generation Cost
                      </p>
                      <p className="font-semibold">Tokens per image</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <input
                      type="number"
                      value={configForm.imageGenerationCost}
                      onChange={(e) =>
                        setConfigForm({
                          ...configForm,
                          imageGenerationCost: e.target.value,
                        })
                      }
                      disabled={configForm.useDynamicTokenDeduction}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 text-2xl font-bold ${themeClasses.input} ${
                        configForm.useDynamicTokenDeduction
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  ) : (
                    <p className="text-3xl font-bold text-purple-400">
                      {chatbotConfig.imageGenerationCost}
                    </p>
                  )}
                </div>

                {/* Dynamic Deduction Toggle */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <RefreshCw className="text-white" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Dynamic Deduction
                      </p>
                      <p className="font-semibold">Use actual AI token usage</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <div className="flex items-center h-full">
                      <button
                        type="button"
                        onClick={() =>
                          setConfigForm({
                            ...configForm,
                            useDynamicTokenDeduction:
                              !configForm.useDynamicTokenDeduction,
                          })
                        }
                        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                          configForm.useDynamicTokenDeduction
                            ? "bg-green-500"
                            : "bg-gray-400"
                        }`}
                      >
                        <span
                          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                            configForm.useDynamicTokenDeduction
                              ? "translate-x-7"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  ) : (
                    <p
                      className={`text-xl font-bold ${chatbotConfig.useDynamicTokenDeduction ? "text-green-500" : "text-gray-500"}`}
                    >
                      {chatbotConfig.useDynamicTokenDeduction
                        ? "Enabled"
                        : "Disabled"}
                    </p>
                  )}
                </div>

                {/* Free Tier Limits */}
                <div
                  className={`${isDarkMode ? "bg-zinc-900" : "bg-gray-50"} rounded-xl p-5`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="text-white" size={20} />
                    </div>
                    <div>
                      <p className={themeClasses.muted + " text-sm"}>
                        Free Tier Limits
                      </p>
                      <p className="font-semibold">Daily Images</p>
                    </div>
                  </div>
                  {editingConfig ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-400">Images</label>
                        <input
                          type="number"
                          value={configForm.freeTierDailyImageLimit}
                          onChange={(e) =>
                            setConfigForm({
                              ...configForm,
                              freeTierDailyImageLimit: e.target.value,
                            })
                          }
                          className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 font-bold ${themeClasses.input}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xl font-bold mt-1">
                        {chatbotConfig.freeTierDailyImageLimit}{" "}
                        <span className="text-sm font-normal text-gray-500">
                          imgs
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div
                className={`mt-6 p-4 rounded-lg ${isDarkMode ? "bg-zinc-800" : "bg-gray-100"}`}
              >
                <p className={themeClasses.muted + " text-sm"}>
                  <strong>Note:</strong> Changes to these settings will take
                  effect immediately. Users with active subscriptions will have
                  higher daily limits based on their plan.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Plan Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className={`${themeClasses.card} border rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto`}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {editingPlan ? "Edit Plan" : "Add New Plan"}
                </h2>
                <button
                  onClick={() => {
                    setShowPlanModal(false);
                    setEditingPlan(null);
                    resetPlanForm();
                  }}
                  className={`${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"} transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSavePlan} className="space-y-4">
                {/* Plan Type */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                  >
                    Plan Type *
                  </label>
                  <select
                    value={planForm.planType}
                    onChange={(e) =>
                      setPlanForm({ ...planForm, planType: e.target.value })
                    }
                    disabled={!!editingPlan}
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  >
                    <option value="">Select plan type</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                {/* Plan Name */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                  >
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={planForm.name}
                    onChange={(e) =>
                      setPlanForm({ ...planForm, name: e.target.value })
                    }
                    placeholder="e.g., Weekly Premium"
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  />
                </div>

                {/* Price and Currency */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                    >
                      Price *
                    </label>
                    <input
                      type="number"
                      value={planForm.price}
                      onChange={(e) =>
                        setPlanForm({ ...planForm, price: e.target.value })
                      }
                      placeholder="99"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                    >
                      Currency
                    </label>
                    <select
                      value={planForm.currency}
                      onChange={(e) =>
                        setPlanForm({ ...planForm, currency: e.target.value })
                      }
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                  </div>
                </div>

                {/* Daily Tokens */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                  >
                    Daily Token Limit *
                  </label>
                  <input
                    type="number"
                    value={planForm.dailyTokens}
                    onChange={(e) =>
                      setPlanForm({ ...planForm, dailyTokens: e.target.value })
                    }
                    placeholder="500"
                    min="1"
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  />
                  <p className={`text-xs mt-1 ${themeClasses.muted}`}>
                    Number of tokens subscriber receives daily (Free users get{" "}
                    {chatbotConfig.dailyFreeCoins})
                  </p>
                </div>

                {/* Daily Image Limits */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                    >
                      Daily Image Limit *
                    </label>
                    <input
                      type="number"
                      value={planForm.dailyImageLimit}
                      onChange={(e) =>
                        setPlanForm({
                          ...planForm,
                          dailyImageLimit: e.target.value,
                        })
                      }
                      placeholder="20"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                    />
                  </div>
                </div>

                {/* Duration and Discount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                    >
                      Duration (days) *
                    </label>
                    <input
                      type="number"
                      value={planForm.durationDays}
                      onChange={(e) =>
                        setPlanForm({
                          ...planForm,
                          durationDays: e.target.value,
                        })
                      }
                      placeholder="7"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                    >
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      value={planForm.discount}
                      onChange={(e) =>
                        setPlanForm({ ...planForm, discount: e.target.value })
                      }
                      placeholder="0"
                      min="0"
                      max="100"
                      className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                  >
                    Description
                  </label>
                  <textarea
                    value={planForm.description}
                    onChange={(e) =>
                      setPlanForm({ ...planForm, description: e.target.value })
                    }
                    placeholder="Plan description..."
                    rows={2}
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  />
                </div>

                {/* Features */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${themeClasses.muted}`}
                  >
                    Features (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={planForm.features}
                    onChange={(e) =>
                      setPlanForm({ ...planForm, features: e.target.value })
                    }
                    placeholder="Unlimited messages, Priority support, No ads"
                    className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-orange-400 ${themeClasses.input}`}
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPlanModal(false);
                      setEditingPlan(null);
                      resetPlanForm();
                    }}
                    disabled={formSubmitting}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-medium border transition-colors disabled:opacity-50 ${
                      isDarkMode
                        ? "border-zinc-700 text-white hover:bg-zinc-800"
                        : "border-gray-300 text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      formSubmitting ||
                      !planForm.planType ||
                      !planForm.name ||
                      !planForm.price ||
                      !planForm.durationDays
                    }
                    className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formSubmitting ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {editingPlan ? "Update Plan" : "Create Plan"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Delete Confirmation Modal */}
        {deleteConfirmation.show && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className={`${themeClasses.card} border rounded-2xl max-w-sm w-full p-6 shadow-2xl transform transition-all scale-100`}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={32} className="text-red-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Delete Plan?</h3>
                <p className={themeClasses.muted}>
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-red-400">
                    {deleteConfirmation.plan?.name}
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() =>
                    setDeleteConfirmation({ show: false, plan: null })
                  }
                  className={`flex-1 px-4 py-2 rounded-lg font-medium border ${
                    isDarkMode
                      ? "border-zinc-700 text-white hover:bg-zinc-800"
                      : "border-gray-300 text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotManagement;
